import { Autocomplete } from '@dnb/eufemia';
import { isObject } from '@portals/shared-frontend/utils';
import type { ComponentPropsWithoutRef, JSX, ReactNode } from 'react';
import { useMemo } from 'react';

/**
 *
 * Drop in replacement for Eufemia's Autocomplete component
 * that handles a `data` object properly
 *
 * TODO: Remove when fixed in Eufemia
 */

export default function FixedAutocomplete({
  data,
  value,
  onChange,
  ...props
}: ComponentPropsWithoutRef<typeof Autocomplete>): JSX.Element {
  const fixedData = useMemo(() => {
    if (!isObject(data)) {
      return data;
    }

    return Object.entries(data).map(([key, value]) => ({
      selectedKey: key,
      selectedValue: value as ReactNode,
      content: value as ReactNode,
    }));
  }, [data]);

  const indices = useMemo(() => {
    const map = new Map<unknown, number>();

    if (isObject(data)) {
      const entries = Object.entries(data);

      for (const [i, [key]] of entries.entries()) {
        map.set(key, i);
      }
    }

    return map;
  }, [data]);

  const fixedOnChange = useMemo(() => {
    if (!isObject(data)) {
      return onChange;
    }

    return (event: Record<string, unknown>) => {
      const rawValue = event['value'];
      const nextValue =
        typeof rawValue === 'number'
          ? Object.keys(data)[rawValue]
          : typeof rawValue === 'string'
            ? rawValue
            : undefined;

      onChange?.({ ...(event as object), value: nextValue } as Parameters<NonNullable<typeof onChange>>[0]);
    };
  }, [data, onChange]);

  const fixedValue = useMemo(() => {
    if (!isObject(data) || value == null) {
      return value;
    }

    return indices.get(value);
  }, [data, indices, value]);

  return <Autocomplete data={fixedData} onChange={fixedOnChange} value={fixedValue} {...props} />;
}
